import {
  getProAdminDataDeletionRequests,
  type ProAdminDataDeletionQuery,
  queryKeys,
  useAuthStore,
} from "@dutyon/apis";
import {
  Alert,
  Button,
  Group,
  Stack,
  Table,
  Text,
  TextInput,
  TitleContainer,
} from "@dutyon/ui-kit";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";
import { deletionDate, prepareDeletionFilters, readDeletionList } from "./model";
export function ProDataDeletionListPage() {
  const actor = useAuthStore((state) => state.user),
    token = useAuthStore((state) => state.token);
  if (!token || !actor || actor.role !== "SUPER_ADMIN")
    return <Alert color="red">최고 관리자만 삭제 요청을 조회할 수 있습니다.</Alert>;
  return <DeletionList key={actor.sub} owner={actor.sub} />;
}
function DeletionList({ owner }: { owner: string }) {
  const [draft, setDraft] = useState({ from: "", to: "" }),
    [filters, setFilters] = useState<ProAdminDataDeletionQuery>({}),
    [message, setMessage] = useState("");
  const history = useInfiniteQuery({
    queryKey: queryKeys.pro.adminDataDeletionRequests(owner, filters),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      let changed = false;
      const unsubscribe = useAuthStore.subscribe((state) => {
        if (state.user?.sub !== owner || state.user.role !== "SUPER_ADMIN") changed = true;
      });
      try {
        const data = await getProAdminDataDeletionRequests(owner, {
          ...filters,
          ...(pageParam ? { cursor: pageParam } : {}),
        });
        const state = useAuthStore.getState();
        if (changed || state.user?.sub !== owner || state.user.role !== "SUPER_ADMIN")
          throw new Error("계정이 변경되었습니다.");
        return readDeletionList(data, pageParam);
      } finally {
        unsubscribe();
      }
    },
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
  });
  const unavailable = history.isError && !history.isFetchNextPageError;
  const rows = unavailable
    ? []
    : [
        ...new Map(
          (history.data?.pages.flatMap((page) => page.items) ?? []).map((row) => [
            row.workspaceId,
            row,
          ]),
        ).values(),
      ];
  return (
    <TitleContainer title="Pro 자료 삭제 요청">
      <Stack>
        <Text>
          Pro 작업공간의 삭제 요청 접수 기록입니다. 전체 탈퇴에 따른 Pro 폐쇄도 포함됩니다. 접수
          기록만으로 물리적 삭제나 환불 완료를 판단하지 않습니다.
        </Text>
        <Text>
          이 화면은 조회만 제공하며 삭제·계정 변경·재처리를 실행하지 않습니다. 실제 삭제 처리와 보존
          자료는 별도 절차에서 확인해야 합니다.
        </Text>
        <Group align="end">
          <TextInput
            type="date"
            label="접수 시작일"
            value={draft.from}
            onChange={(event) => setDraft({ ...draft, from: event.currentTarget.value })}
          />
          <TextInput
            type="date"
            label="접수 종료일"
            value={draft.to}
            onChange={(event) => setDraft({ ...draft, to: event.currentTarget.value })}
          />
          <Button
            disabled={history.isFetching}
            onClick={() => {
              try {
                setFilters(prepareDeletionFilters(draft.from, draft.to));
                setMessage("");
              } catch {
                setMessage("조회 날짜와 순서를 확인해 주세요.");
              }
            }}
          >
            조회 적용
          </Button>
          <Button
            variant="light"
            disabled={history.isFetching}
            onClick={() => {
              setDraft({ from: "", to: "" });
              setFilters({});
              setMessage("");
            }}
          >
            조건 초기화
          </Button>
        </Group>
        <Text size="sm">접수 날짜는 한국시간으로 조회하며 종료일을 포함합니다.</Text>
        {message && <Alert color="yellow">{message}</Alert>}
        {history.isPending && <Text role="status">삭제 요청을 조회하고 있습니다.</Text>}
        {history.isError && <Alert color="red">삭제 요청 목록을 불러오지 못했습니다.</Alert>}
        {!history.isPending && !history.isError && rows.length === 0 && (
          <Text>조회 조건에 해당하는 삭제 요청이 없습니다.</Text>
        )}
        {rows.length > 0 && (
          <Table.ScrollContainer minWidth={650}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>작업공간 번호</Table.Th>
                  <Table.Th>상태</Table.Th>
                  <Table.Th>접수 시각</Table.Th>
                  <Table.Th>폐쇄 시각</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((row) => (
                  <Table.Tr key={row.workspaceId}>
                    <Table.Td>{row.workspaceId}</Table.Td>
                    <Table.Td>삭제 요청 접수</Table.Td>
                    <Table.Td>{deletionDate(row.requestedAt)}</Table.Td>
                    <Table.Td>{deletionDate(row.closedAt)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
        {history.hasNextPage && (
          <Button
            loading={history.isFetchingNextPage}
            disabled={history.isFetching}
            onClick={() => void history.fetchNextPage()}
          >
            다음 요청 더 보기
          </Button>
        )}
        <Button
          variant="light"
          disabled={history.isFetching}
          onClick={() => void history.refetch()}
        >
          목록 다시 조회
        </Button>
      </Stack>
    </TitleContainer>
  );
}
